import { useCallback, useEffect, useMemo, useState } from "react";
import type { Editor } from "@tiptap/core";
import {
  TextInput,
  Stack,
  Switch,
  Button,
  Group,
  Badge,
  Text,
  Paper,
  Box,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";

import { GrossCode } from "../../../services/admin/grosscodes.service";
import { extractVariables, stripHtml } from "../../../utils/grossTemplate";
import { required, scrollToFirstError } from "../../../utils/formValidation";
import { getFragmentCountDisplayValue } from "../../../utils/fragmentCount";
import AdminFragmentCountInput, {
  parseAdminFragmentCountDraft,
} from "./AdminFragmentCountInput";

/**
 * Predefined available variables for accessioning gross code templates.
 * These variables are replaced with actual values when the gross code is applied.
 */
const AVAILABLE_VARIABLES = [
  { name: "specimen_count", description: "Number of specimens", example: "3" },
  {
    name: "specimen_letter",
    description: "Specimen designation",
    example: "A",
  },
  { name: "source", description: "Tissue source", example: "Colon" },
  { name: "subsite", description: "Specific location", example: "Ascending" },
  {
    name: "collection_method",
    description: "How collected",
    example: "Polypectomy",
  },
  { name: "size_cm", description: "Size in centimeters", example: "2.5" },
  { name: "cassette_count", description: "Number of cassettes", example: "2" },
];

// Generate sample data for preview from available variables
const SAMPLE_DATA: Record<string, string> = AVAILABLE_VARIABLES.reduce(
  (acc, v) => ({ ...acc, [v.name]: v.example }),
  {}
);

/**
 * Replace variables in template with provided values
 */
function replaceVariables(
  description: string,
  variables: Record<string, string>
): string {
  let result = description;
  Object.entries(variables).forEach(([key, value]) => {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, "g");
    result = result.replace(regex, value);
  });
  return result;
}

export interface GrossCodeFormValues {
  code: string;
  description: string;
  isActive: boolean;
  fragmentCount?: number | null;
  fragmentCountQualifier?: "multiple" | null;
}

interface GrossCodeDraftFormValues {
  code: string;
  description: string;
  isActive: boolean;
  fragmentCountDraft: string;
}

interface GrossCodeFormProps {
  initialValues?: GrossCode;
  onSubmit: (values: GrossCodeFormValues) => void;
}

export default function GrossCodeForm({
  initialValues,
  onSubmit,
}: GrossCodeFormProps) {
  const [showPreview, setShowPreview] = useState(false);

  const form = useForm<GrossCodeDraftFormValues>({
    initialValues: {
      code: initialValues?.code || "",
      description: initialValues?.description || "",
      isActive: initialValues?.isActive ?? true,
      fragmentCountDraft: getFragmentCountDisplayValue(
        initialValues?.fragmentCount ?? null,
        initialValues?.fragmentCountQualifier ?? null
      ),
    },
    validate: {
      code: required("Gross code is required"),
      description: (value) => {
        // Rich text content needs to be stripped of HTML to check if empty
        const plainText = stripHtml(value || "").trim();
        return !plainText ? "Description is required" : null;
      },
      fragmentCountDraft: (value) => parseAdminFragmentCountDraft(value).error,
    },
  });

  const handleFragmentCountChange = useCallback(
    (value: string) => form.setFieldValue("fragmentCountDraft", value),
    [form]
  );

  const handleFormSubmit = useCallback(
    (values: GrossCodeDraftFormValues) => {
      const parsed = parseAdminFragmentCountDraft(values.fragmentCountDraft);
      if (!parsed.patch) return;
      onSubmit({
        code: values.code,
        description: values.description,
        isActive: values.isActive,
        ...parsed.patch,
      });
    },
    [onSubmit]
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder:
          "Enter gross code description. Use {{variable_name}} for variables...",
      }),
    ],
    content: form.values.description || "",
    onUpdate: ({ editor: updateEditor }: { editor: Editor | null }) => {
      if (!updateEditor) return;
      const html = updateEditor.getHTML();
      form.setFieldValue("description", html);
    },
  }) as Editor | null;

  // Extract variables from current description for display
  const detectedVariables = useMemo(() => {
    return extractVariables(form.values.description);
  }, [form.values.description]);

  // Generate preview with sample data
  const previewDescription = useMemo(() => {
    if (!form.values.description) return "";
    return replaceVariables(form.values.description, SAMPLE_DATA);
  }, [form.values.description]);

  // Insert a predefined variable at cursor position (with spaces for readability)
  const handleInsertPredefinedVariable = (varName: string) => {
    if (editor) {
      editor.chain().focus().insertContent(` {{${varName}}} `).run();
    }
  };

  // Update editor when initial values change
  useEffect(() => {
    if (
      editor &&
      initialValues?.description &&
      editor.getHTML() !== initialValues.description
    ) {
      editor.commands.setContent(initialValues.description);
    }
  }, [initialValues?.description, editor]);

  return (
    <form
      noValidate
      onSubmit={form.onSubmit(handleFormSubmit, () => scrollToFirstError(form))}
    >
      <Stack gap="md">
        <TextInput
          label="Code"
          placeholder="e.g., BX, POL, EXC"
          withAsterisk
          {...form.getInputProps("code")}
        />

        <AdminFragmentCountInput
          value={form.values.fragmentCountDraft}
          onChange={handleFragmentCountChange}
          error={form.errors.fragmentCountDraft}
        />

        {/* Available Variables Helper - BEFORE the editor */}
        <Paper withBorder p="sm">
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Available Variables (click to insert)
            </Text>
            <Group gap={6} wrap="wrap">
              {AVAILABLE_VARIABLES.map((variable) => (
                <Tooltip
                  key={variable.name}
                  label={`${variable.description} (e.g., "${variable.example}")`}
                  position="top"
                  withArrow
                >
                  <Badge
                    variant="light"
                    color="indigo"
                    size="md"
                    style={{ cursor: "pointer", fontFamily: "monospace" }}
                    onClick={() =>
                      handleInsertPredefinedVariable(variable.name)
                    }
                  >
                    {`{{${variable.name}}}`}
                  </Badge>
                </Tooltip>
              ))}
            </Group>
          </Stack>
        </Paper>

        {/* Rich Text Description Template */}
        <div>
          <Text component="label" fz="sm" fw={500} mb={5}>
            Description Template{" "}
            <Text span c="red">
              *
            </Text>
          </Text>
          <Text size="xs" c="dimmed" mb="xs">
            Use {"{{variable_name}}"} syntax for dynamic fields. Supports bold,
            italic, underline, and lists.
          </Text>
          <RichTextEditor
            id="gross-description"
            editor={editor}
            style={
              form.errors.description
                ? { borderColor: "var(--mantine-color-red-6)" }
                : undefined
            }
          >
            <RichTextEditor.Toolbar sticky stickyOffset={60}>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.ClearFormatting />
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>

            <RichTextEditor.Content style={{ minHeight: "200px" }} />
          </RichTextEditor>
          {form.errors.description && (
            <Text size="xs" c="red" mt={5}>
              {form.errors.description}
            </Text>
          )}
        </div>

        {/* Detected Variables & Preview Toggle */}
        {detectedVariables.length > 0 && (
          <Paper withBorder p="sm" bg="blue.0">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="xs" fw={600} c="blue">
                  Detected Variables ({detectedVariables.length}):
                </Text>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? "Hide Preview" : "Show Preview"}
                </Button>
              </Group>
              <Group gap="xs">
                {detectedVariables.map((variable) => (
                  <Badge
                    key={variable}
                    variant="light"
                    color="blue"
                    style={{ fontFamily: "monospace" }}
                  >
                    {`{{${variable}}}`}
                  </Badge>
                ))}
              </Group>
              <Text size="xs" c="dimmed">
                These will be replaced with actual values during accessioning
              </Text>
            </Stack>
          </Paper>
        )}

        {/* Preview Section */}
        {showPreview && form.values.description && (
          <Paper withBorder p="sm" bg="gray.0">
            <Stack gap="xs">
              <Text size="xs" fw={600} c="dimmed">
                Preview (with sample data):
              </Text>
              <Box
                style={{ fontSize: "14px" }}
                dangerouslySetInnerHTML={{ __html: previewDescription }}
              />
            </Stack>
          </Paper>
        )}

        <Switch
          label="Active"
          description="Inactive codes will not appear in dropdowns"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
